import React, { useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useContentCheckAssignment } from '../../ContentCheckPolicyManager/hooks/useContentCheckAssignment';

/**
 * TemplatePoliciesList Component
 *
 * Displays a list of content check policies assigned to the template.
 * Shows in the sidebar of the template editor.
 *
 * @param {Object} props - Component props
 * @param {string} props.templateId - Template ID
 * @param {Function} props.onManagePolicies - Callback to open policy management dialog
 */
const TemplatePoliciesList = ({ templateId, onManagePolicies }) => {
  const { assignments, loading, error, loadAssignments } =
    useContentCheckAssignment(templateId);

  useEffect(() => {
    if (templateId && templateId !== 'new') {
      loadAssignments();
    }
  }, [templateId, loadAssignments]);

  if (!templateId || templateId === 'new') {
    return null;
  }

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  const enabledAssignments = assignments.filter(a => a.enabled);
  const hasPolicies = enabledAssignments.length > 0;

  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Security Policies
          </Typography>
          {hasPolicies && (
            <Chip
              label={enabledAssignments.length}
              size="small"
              color="success"
              sx={{ height: '20px', fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <Tooltip title="Manage Policies">
          <IconButton
            size="small"
            onClick={onManagePolicies}
            sx={{ color: 'primary.main' }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {hasPolicies ? (
        <List dense sx={{ p: 0 }}>
          {enabledAssignments.map(assignment => (
            <ListItem
              key={assignment.assignment_id}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: 1,
                }}
              >
                <ShieldIcon
                  sx={{
                    fontSize: '1rem',
                    color: 'success.main',
                  }}
                />
                <ListItemText
                  primary={assignment.policy_name || 'Unknown Policy'}
                  secondary={
                    assignment.action_on_violation
                      ? `Action: ${assignment.action_on_violation}`
                      : null
                  }
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500 },
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                  }}
                />
              </Box>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 2,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            No policies assigned
          </Typography>
          <Typography variant="caption">
            Click the settings icon to manage policies
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default TemplatePoliciesList;
